import { useState, useEffect } from "react";
import { Button, Group, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconSend } from "@tabler/icons-react";
import {
  useActiveDeliveryMethodsForCase,
  useQueueManualDelivery,
} from "../../hooks/useWorkflows";
import { DeliveryNoMethodsContent } from "./DeliveryNoMethodsContent";
import { DeliveryMethodChecklistContent } from "./DeliveryMethodChecklistContent";

interface SendReportModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  physicianId: string | null;
  sendingPracticeId: string | null;
}

export function SendReportModal({
  opened,
  onClose,
  caseId,
  physicianId,
  sendingPracticeId,
}: SendReportModalProps) {
  const { data: methods, isLoading } = useActiveDeliveryMethodsForCase(
    opened ? caseId : undefined
  );
  const queueMutation = useQueueManualDelivery();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Pre-select all when methods load
  useEffect(() => {
    if (methods) {
      setSelectedIds(methods.map((m) => m.id));
    }
  }, [methods]);

  const handleSend = async () => {
    if (selectedIds.length === 0) return;
    try {
      const result = await queueMutation.mutateAsync({
        caseId,
        deliveryConfigIds: selectedIds,
      });
      const count = result.queued?.length ?? selectedIds.length;
      notifications.show({
        color: "green",
        title: "Report Queued",
        message: `${count} delivery item${
          count !== 1 ? "s" : ""
        } queued for processing.`,
      });
      onClose();
    } catch {
      notifications.show({
        color: "red",
        title: "Send Failed",
        message: "Failed to queue report deliveries. Please try again.",
      });
    }
  };

  const hasNoMethods = !isLoading && methods && methods.length === 0;
  const hasMultipleMethods = !isLoading && methods && methods.length >= 2;

  return (
    <Modal.Root opened={opened} onClose={onClose} size="md">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Send Report</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          {isLoading && (
            <Group justify="center" py="lg">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading delivery methods...
              </Text>
            </Group>
          )}

          {hasNoMethods && (
            <DeliveryNoMethodsContent
              physicianId={physicianId}
              practiceId={sendingPracticeId}
            />
          )}

          {hasMultipleMethods && (
            <Stack gap="lg">
              <DeliveryMethodChecklistContent
                methods={methods}
                selectedIds={selectedIds}
                onChange={setSelectedIds}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  color="gcpBlue"
                  leftSection={<IconSend size={16} />}
                  onClick={handleSend}
                  loading={queueMutation.isPending}
                  disabled={selectedIds.length === 0}
                >
                  Send
                </Button>
              </Group>
            </Stack>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
